import { Injectable } from '@angular/core';
import { Business, BusinessAddress } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
} from '@models/matter';
import { isSet } from '@core/util/value';
import { addressString } from '@core/util/address';
import {
	clientIsPerson,
	clientLegalName,
	isGuarantor,
} from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { formatCentsToDollars } from '@core/util/money';
import { format } from 'date-fns';
import { GetDateObject } from '@core/util/datetime';
import { TitleInsurancePolicyType } from '@models/title-insurance';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class RBC_RESIDENTIAL_FINAL_REPORT_Mapper extends BaseFormMapper<RBC_RESIDENTIAL_FINAL_REPORT> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: RBC_RESIDENTIAL_FINAL_REPORT,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): RBC_RESIDENTIAL_FINAL_REPORT {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as RBC_RESIDENTIAL_FINAL_REPORT);

		if (!isSet(_formData?.reportType)) {
			_formData.reportType = '2';
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.lenderReferenceNumber)) {
			_formData.lenderReferenceNumber = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.mortgagors)) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);

			const Contacts = FilteredMatterClients.filter((v) =>
				clientIsPerson(v),
			) as PersonMatterClientWithAssociations[];
			_formData.mortgagors = joinNames(
				Contacts.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.guarantors && !isSet(_formData?.guarantors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.guarantors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.propertyAddress)
		) {
			_formData.propertyAddress = addressString(
				matter.propertyDetails.address,
				true,
			);
		}

		if (
			mortgage?.registrationDate &&
			!isSet(_formData?.mortgageRegistrationDate)
		) {
			_formData.mortgageRegistrationDate = format(
				GetDateObject(mortgage.registrationDate),
				'MMMM dd, yyyy',
			);
		}

		if (
			mortgage?.registrationNumber &&
			!isSet(_formData?.mortgageRegistrationNumber)
		) {
			_formData.mortgageRegistrationNumber = mortgage.registrationNumber;
		}

		if (mortgage?.amount && !isSet(_formData?.mortgageAmount)) {
			_formData.mortgageAmount = formatCentsToDollars(mortgage.amount);
		}

		if (
			matter?.titleInsurance?.companyName &&
			!isSet(_formData?.titleInsurerName)
		) {
			_formData.titleInsurerName = matter.titleInsurance.companyName;
		}

		const lenderPolicy = matter?.titleInsurance?.policies?.find(
			(policy) =>
				policy.type === TitleInsurancePolicyType.LENDER &&
				policy.priority === mortgage?.priority,
		);

		if (lenderPolicy && !isSet(_formData?.titleInsuranceReferenceNumber)) {
			_formData.titleInsuranceReferenceNumber = lenderPolicy.number;
		}

		if (matter?.lawyerAssigned && !isSet(_formData?.lawyerName)) {
			_formData.lawyerName = matter.lawyerAssigned.name;
		}

		return _formData;
	}

	MapValues(): Partial<RBC_RESIDENTIAL_FINAL_REPORT> {
		return {};
	}
}

const Mappings: RBC_RESIDENTIAL_FINAL_REPORT = {
	// "0":"topmostSubform[0]",
	// "1":"topmostSubform[0].Page2[0]",
	exceptions: 'topmostSubform[0].Page2[0].EXCEPTIONS[0]',
	// "3":"topmostSubform[0].Page1[0]",
	// "4":"topmostSubform[0].Page1[0].CREANCIER_FAX[0]",
	lawyerName: 'topmostSubform[0].Page1[0].NOTAIRE_NOM_COMPLET[0]',
	// "6":"topmostSubform[0].Page1[0].PROVINCE[0]",
	titleInsurerName: 'topmostSubform[0].Page1[0].AssuranceCie[0]',
	titleInsuranceReferenceNumber: 'topmostSubform[0].Page1[0].AssuranceTitre[0]',
	checkProtocol: 'topmostSubform[0].Page1[0].CHK_Protocol[0]',
	checkPolicy: 'topmostSubform[0].Page1[0].CHK_Policy[0]',
	checkDocument: 'topmostSubform[0].Page1[0].Chk_Document[0]',
	titleOpinion: 'topmostSubform[0].Page1[0].Opinions[0]',
	taxesPropertyCode: 'topmostSubform[0].Page1[0].ROLL_NUMBER_P1[0]',
	taxesPaidInFullDate: 'topmostSubform[0].Page1[0].paidInFullToDate[0]',
	mortgageRegistrationDate:
		'topmostSubform[0].Page1[0].DATE_PUBLICATION_PRET[0]',
	mortgageRegistrationNumber:
		'topmostSubform[0].Page1[0].NO_PUBLICATION_PRET[0]',
	mortgageAmount: 'topmostSubform[0].Page1[0].TOT_PRCDS[0]',
	// "18":"topmostSubform[0].Page1[0].NUM_LOT_4177[0]",
	propertyAddress: 'topmostSubform[0].Page1[0].ADRESSE_PROPRIETE[0]',
	guarantors: 'topmostSubform[0].Page1[0].GUA_LINE_4177[0]',
	mortgagors: 'topmostSubform[0].Page1[0].COB_LINE_4177[0]',
	// "22":"topmostSubform[0].Page1[0].CREANCIER_CITYPROVPCODE[0]",
	// "23":"topmostSubform[0].Page1[0].CREANCIER_ADRESSE_SUCC[0]",
	// "24":"topmostSubform[0].Page1[0].MC_NAMEXX[0]",
	// "25":"topmostSubform[0].Page1[0].RANG_PRET_FORMAT[0]",
	reportType: 'topmostSubform[0].Page1[0].ReportType[0]',
	bankTitle: 'topmostSubform[0].Page1[0].BankTitle[0]',
	date: 'topmostSubform[0].Page1[0].DATE_DOC_GENERATION[0]',
	lenderReferenceNumber: 'topmostSubform[0].Page1[0].MORTREFNO[0]',
	// "30":"topmostSubform[0].Page1[0].BARCODE_FINAL_REPORT[0]"
};

export interface RBC_RESIDENTIAL_FINAL_REPORT {
	lenderReferenceNumber: string;
	date: string;
	mortgagors: string;
	guarantors: string;

	propertyAddress: string;

	mortgageAmount: string;
	mortgageRegistrationNumber: string;
	mortgageRegistrationDate: string;
	taxesPaidInFullDate: string;
	taxesPropertyCode: string;
	titleOpinion: string;
	titleInsuranceReferenceNumber: string;
	titleInsurerName: string;
	lawyerName: string;
	exceptions: string;

	bankTitle: string;
	reportType: string;

	checkDocument: string;
	checkPolicy: string;
	checkProtocol: string;
}
